import { __awaiter, __decorate, __extends, __generator, __metadata } from "tslib";
import NeoModel from '../../Model/NeoModel';
import { ModalUtils } from '../Modal/ModalUtils';
import { Observable } from '../../Model/Decorators';
import { Globals } from '../../Misc';
import { NeoTypes } from '../../AppServices';
import { ErrorDisplayType } from '../ErrorHandler';
import { Settings } from '../../Settings';
import { FileUploadBatch } from './FileUploadBatch';
import NeoStrings from '../../Localisation/NeoStrings';
var fileInputSymbol = Symbol("FileInput");
var FileManagerBase = /** @class */ (function () {
    function FileManagerBase(uploadEndpointOrOptions, options) {
        this.currentUploads = [];
        if (uploadEndpointOrOptions === undefined || typeof uploadEndpointOrOptions === "string") {
            this._uploadEndpoint = uploadEndpointOrOptions;
            this.options = options || {};
        }
        else {
            this.options = uploadEndpointOrOptions;
        }
    }
    Object.defineProperty(FileManagerBase.prototype, "isUploading", {
        get: function () {
            return this.currentUploads.length > 0;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FileManagerBase.prototype, "uploadPercent", {
        get: function () {
            var totalSize = 0;
            var totalProgress = 0;
            for (var _i = 0, _a = this.currentUploads; _i < _a.length; _i++) {
                var batch = _a[_i];
                totalSize += batch.totalSize;
                totalProgress += batch.totalSize * batch.uploadPercent;
            }
            return totalSize === 0 ? 1 : (totalProgress / totalSize);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FileManagerBase.prototype, "uploadEndpoint", {
        get: function () {
            return this._uploadEndpoint;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FileManagerBase.prototype, "allowMultiple", {
        get: function () {
            return false;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Shows a file dialog and then calls processFiles.
     * onFilesSelected will be called if you need to handle the event.
     */
    FileManagerBase.prototype.showFileDialog = function () {
        var fileInput = document.getElementById("neo-file-input");
        if (!fileInput) {
            // Its 2020, and this is still the only way to open a file select dialog programmatically.
            fileInput = document.createElement("input");
            fileInput.type = "file";
            fileInput.id = "neo-file-input";
            fileInput.style.display = "none";
            fileInput.addEventListener("change", function (e) {
                // @ts-ignore
                var fileManager = fileInput[fileInputSymbol];
                if (fileInput.files) {
                    fileManager.processFiles(fileInput.files, { handleErrors: !fileManager.options.taskRunner }).finally(function () {
                        fileInput.value = "";
                    });
                }
            });
            document.body.appendChild(fileInput);
        }
        // @ts-ignore
        fileInput[fileInputSymbol] = this;
        this.setFileInputValues(fileInput);
        fileInput.click();
    };
    /** Checks if the file extensions are allowed. Shows a modal if not. */
    FileManagerBase.prototype.validateFiles = function (files) {
        var checkResult = this.checkFileExtensions(files);
        if (checkResult.allowed) {
            return true;
        }
        else {
            ModalUtils.showMessage(NeoStrings.fileManager.uploadErrorTitle, NeoStrings.fileManager.uploadErrorBody.format(checkResult.extension));
            return false;
        }
    };
    /**
     * Checks if file extensions are allowed, then calls onFilesSelected, or uploadFiles
     */
    FileManagerBase.prototype.processFiles = function (files_1) {
        return __awaiter(this, arguments, void 0, function (files, options) {
            if (options === void 0) { options = {}; }
            return __generator(this, function (_a) {
                if (files.length > 1 && !this.allowMultiple) {
                    files = [Array.from(files)[0]];
                }
                if (this.validateFiles(files)) {
                    if (this.options.onFilesSelected) {
                        return [2 /*return*/, Promise.resolve(this.options.onFilesSelected(files, this))];
                    }
                    else if (this.uploadEndpoint) {
                        if (!options.additionalData && this.options.additionalData) {
                            if (typeof this.options.additionalData === "function") {
                                options.additionalData = this.options.additionalData();
                            }
                            else {
                                options.additionalData = this.options.additionalData;
                            }
                        }
                        return [2 /*return*/, this.uploadFiles(files, options)];
                    }
                    else {
                        throw Error("You must specify at least one of uploadEndpoint, or onFilesSelected.");
                    }
                }
                return [2 /*return*/];
            });
        });
    };
    FileManagerBase.prototype.uploadFiles = function (files, options) {
        var _this = this;
        var _a;
        if (options === void 0) { options = {}; }
        if (!this.uploadEndpoint) {
            throw Error("uploadFiles() can only be called if an uploadEndpoint has been specified.");
        }
        if (!this.options.allowConcurrent && this.isUploading) {
            throw Error("A file upload is already in progress, please wait for the current operation to complete.");
        }
        var formData = new FormData();
        if (options.additionalData) {
            for (var key in options.additionalData) {
                formData.append(key, options.additionalData[key].toString());
            }
        }
        var uploadBatch;
        if (files instanceof FileUploadBatch) {
            uploadBatch = files;
        }
        else {
            uploadBatch = new FileUploadBatch(files);
        }
        this.currentUploads.push(uploadBatch);
        for (var i = 0; i < uploadBatch.files.length; i++) {
            formData.append("file" + i, uploadBatch.files[i]);
        }
        var config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: function (progressEvent) {
                if (progressEvent.total && progressEvent.total > 0) {
                    uploadBatch.uploadPercent = progressEvent.loaded / progressEvent.total;
                }
                if (_this.options.taskRunner) {
                    // Default to 90% of progress because it seems to hit 100% a while before the axios promise resolves.
                    _this.options.taskRunner.progressState.progress = _this.uploadPercent * (_this.options.percentOfTask || 0.9);
                }
            }
        };
        var uploadTask = new Promise(function (resolve, reject) {
            // Send data to server
            Globals.appService.get(NeoTypes.Axios).post(_this.uploadEndpoint, formData, config).then(function (response) {
                _this.afterUpload(response.data);
                if (_this.options.afterUpload) {
                    _this.options.afterUpload(response.data);
                }
                resolve(response.data);
            }).catch(function (e) {
                if (options.handleErrors) {
                    Globals.appService.get(NeoTypes.UI.ErrorHandler).showError(e, ErrorDisplayType.ShowModal);
                }
                uploadBatch.uploadError = e.toString();
                reject(e);
            }).finally(function () {
                uploadBatch.isComplete = true;
                _this.currentUploads.remove(uploadBatch);
            });
        });
        if (this.options.taskRunner) {
            this.options.taskRunner.waitFor(uploadTask, { loadingText: (_a = this.options.taskRunnerLoadingText) === null || _a === void 0 ? void 0 : _a.format(uploadBatch.toString()), autoProgress: false });
        }
        return uploadTask;
    };
    FileManagerBase.prototype.setFileInputValues = function (fileInput) {
        var props = this.getFileInputProps();
        for (var attribute in props) {
            // @ts-ignore
            fileInput[attribute] = props[attribute];
        }
        fileInput.value = "";
    };
    FileManagerBase.prototype.getFileInputProps = function () {
        var props = {};
        if (this.options.accept) {
            props.accept = this.options.accept;
        }
        else if (this.options.allowedExtensions) {
            props.accept = this.options.allowedExtensions.join();
        }
        else if (Settings.fileManager.allowedExtensions.length) {
            props.accept = Settings.fileManager.allowedExtensions.join();
        }
        else {
            props.accept = "";
        }
        return props;
    };
    FileManagerBase.prototype.afterUpload = function (response) {
    };
    /** Checks if the file extensions are allowed. */
    FileManagerBase.prototype.checkFileExtensions = function (files) {
        var _a;
        for (var _i = 0, _b = Array.from(files); _i < _b.length; _i++) {
            var file = _b[_i];
            var extension = void 0;
            if (file.name.indexOf(".") > 0) {
                extension = "." + ((_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase());
                if (this.options.allowedExtensions) {
                    if (this.options.allowedExtensions.indexOf(extension) < 0) {
                        return { allowed: false, extension: extension };
                    }
                }
                else {
                    if (Settings.fileManager.allowedExtensions.length && Settings.fileManager.allowedExtensions.indexOf(extension) < 0) {
                        return { allowed: false, extension: extension };
                    }
                    if (Settings.fileManager.disallowedFileExtensions.length && Settings.fileManager.disallowedFileExtensions.indexOf(extension) >= 0) {
                        return { allowed: false, extension: extension };
                    }
                }
            }
            else {
                return { allowed: false, extension: "(none)" };
            }
        }
        return { allowed: true, extension: "" };
    };
    __decorate([
        Observable(),
        __metadata("design:type", Array)
    ], FileManagerBase.prototype, "currentUploads", void 0);
    return FileManagerBase;
}());
export { FileManagerBase };
var FileManager = /** @class */ (function (_super) {
    __extends(FileManager, _super);
    function FileManager() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    FileManager.prototype.getFileInputProps = function () {
        var props = _super.prototype.getFileInputProps.call(this);
        props.multiple = this.options.multiple === true;
        return props;
    };
    Object.defineProperty(FileManager.prototype, "allowMultiple", {
        get: function () {
            var _a;
            return (_a = this.options.multiple) !== null && _a !== void 0 ? _a : false;
        },
        enumerable: false,
        configurable: true
    });
    FileManager = __decorate([
        NeoModel
    ], FileManager);
    return FileManager;
}(FileManagerBase));
export default FileManager;
